import { useEffect } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { goToLogin } from "@/auth/sso";
import Logo from "@/components/Logo";
import Landing from "@/pages/Landing";
import CourseHub from "@/pages/CourseHub";
import CourseExercises from "@/pages/CourseExercises";
import Exercise from "@/pages/Exercise";
import Instructor from "@/pages/Instructor";
import InstructorShell from "@/components/InstructorShell";
import PackEditor from "@/pages/instructor/PackEditor";
import InstructorDashboard from "@/pages/instructor/InstructorDashboard";
import CoursesList from "@/pages/instructor/CoursesList";
import CourseDetail from "@/pages/instructor/CourseDetail";
import InstructorSettings from "@/pages/instructor/InstructorSettings";
import Playground from "@/pages/Playground";
import Login from "@/pages/Login";

function RequireInstructor({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return <div style={{ padding: 32 }}>Chargement…</div>;
  if (!user?.is_instructor) return <Navigate to="/" replace />;
  return <>{children}</>;
}

function RequireAuth({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  // Non connecté → on délègue au service d'identité (compte.orense.ch), qui
  // ramène ici après login (cookie SSO partagé). Pas de page de login locale.
  useEffect(() => {
    if (!loading && !user?.authenticated) goToLogin();
  }, [loading, user]);
  if (loading || !user?.authenticated) return <AuthSplash />;
  return <>{children}</>;
}

/** Splash neutre tant que l'auth n'est pas résolue — évite tout aperçu d'une page
 *  protégée avant la redirection vers /login (pas de « flash » au chargement). */
function AuthSplash() {
  return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "var(--bg-base)" }}>
      <Logo size={48} />
    </div>
  );
}

export default function App() {
  // Gate global : on n'affiche AUCUNE route (même /login) avant la réponse de /me.
  const { loading } = useAuth();
  if (loading) return <AuthSplash />;

  return (
    <Routes>
      <Route path="/" element={<RequireAuth><Landing /></RequireAuth>} />
      <Route path="/login"       element={<Login />} />
      <Route path="/exercises" element={<RequireAuth><CourseHub /></RequireAuth>} />
      <Route path="/exercises/c/:courseId" element={<RequireAuth><CourseExercises /></RequireAuth>} />
      <Route path="/exercises/:id" element={<Exercise />} />
      <Route
        path="/playground"
        element={
          <RequireAuth>
            <Playground />
          </RequireAuth>
        }
      />
      {/* Espace enseignant : coque à sidebar (Dashboard / Cours / Archives / Réglages). */}
      <Route element={<RequireInstructor><InstructorShell /></RequireInstructor>}>
        <Route path="/instructor" element={<Navigate to="/instructor/courses" replace />} />
        <Route path="/instructor/dashboard" element={<InstructorDashboard />} />
        <Route path="/instructor/courses" element={<CoursesList />} />
        <Route path="/instructor/archives" element={<CoursesList archived />} />
        <Route path="/instructor/courses/:courseId" element={<CourseDetail />} />
        <Route path="/instructor/settings" element={<InstructorSettings />} />
        {/* Éditeur de contenu : dans la coque mais en « mode focus » (sidebar masquée). */}
        <Route path="/instructor/c/:courseId" element={<Instructor />} />
        {/* PackEditor : édite un pack de jeu (multi-fichiers Python), cf. plan gamelib P1.5. */}
        <Route path="/instructor/c/:courseId/packs/:packId" element={<PackEditor />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
